import { Fragment, useCallback, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ContentPaste } from "@mui/icons-material";
import { useSelectionContext } from "../../../../context/SelectionContext";
import { CollapsibleItem } from "../../../../components/shared/sidebar/CollapsibleItem";
import { tableSectionToIconMap } from "../TableActionBar";
import { type PlanTableFormat } from "../../../../types/planning/plan.type";
import { filterPlanTableFormatBySearchTerm } from "../../inboundPlanning/utils";
import type { SummaryContext } from "../ActionsMenuContext";
import { unifyShipmentContext } from "../../../../utils/unifyShipmentContext";
import { useValidateMergeRequest } from "../../../../hooks/useValidateMergeRequest";
import { planningContextMenuActions } from "../../inboundPlanning/PlanningContextMenu/planningContextMenuActions";
import ContextMenuDropdown, {
  type ContextMenuRef,
} from "../../../../components/shared/ContextMenuDropdown";
import Filters from "../Filter/Filters";
import { useFilterSearchParams } from "../../../../hooks/useFilterSearchParams";

import type { ServiceCenter } from "../../../../types/serviceCenter.type";
import {
  filterPlannedSidebarRoutesAndTraps,
  plannedSidebarRoutesAndTrapsFilters,
} from "../../../../constants/filters/plannedSidebarRoutesAndTrapsFilters";
import { SearchBox } from "../../../../components/shared/SearchBox";
import { useLinkedFilters } from "../../../../hooks/useLinkedFilters";
import { useIsFeatureFlagEnabled } from "../../../../featureFlags/useIsFeatureFlagEnabled";

import PlanningDroppableSectionItem from "./PlanningDroppableSectionItem";
import { NoResultFound } from "./SectionItem";

type PlannedRoutesProps = {
  routes: PlanTableFormat[];
  selectedDate: string;
  serviceCenter: ServiceCenter;
  isEditingAllowed: boolean;
};

export const PlannedRoutes = ({
  routes,
  isEditingAllowed,
  selectedDate,
  serviceCenter,
}: PlannedRoutesProps) => {
  const contextMenuRef = useRef<ContextMenuRef<SummaryContext>>(null);

  const [searchParams] = useSearchParams();

  const { selected, clearSelection, isAllSelected } = useSelectionContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isCNCLTrapFeatureEnabled = useIsFeatureFlagEnabled(
    "operations-5594-CNCL-traps-treatment-client"
  );

  const isFiltersLinked = useLinkedFilters();

  const [filters] = useFilterSearchParams(
    {
      zone: [],
      location: [],
      startTime: [],
      endTime: [],
      routeEnd: [],
      bills: [],
      pallets: [],
      weight: [],
      pieces: [],
      handlingUnits: [],
      trailerName: [],
      tractorName: [],
      driverName: [],
      name: [],
      status: [],
      tags: [],
    },
    !isFiltersLinked ? "routes" : "linked"
  );

  const filteredPlans = filterPlannedSidebarRoutesAndTraps(
    routes,
    serviceCenter.timeZone,
    filters
  );

  const filteredRoutes = filterPlanTableFormatBySearchTerm(
    filteredPlans,
    serviceCenter.timeZone,
    searchTerm
  );

  const selectionToContext = useCallback(
    (plan?: SummaryContext): SummaryContext[] => {
      if (plan) {
        return [
          {
            id: String(plan.id),
            shipmentIds: plan.shipmentIds,
            type: plan.type,
          },
        ];
      }
      return unifyShipmentContext(
        selected.map(({ id }) => {
          const plan = filteredRoutes.find((plan) => plan.id.toString() === id);
          return {
            id,
            shipmentIds: plan ? plan.shipmentIds : [],
            type: plan
              ? plan.type === "delivery_route"
                ? "route"
                : "trap"
              : "route",
          };
        })
      );
    },
    [filteredRoutes, selected]
  );
  const selectedContext = selectionToContext();

  const { validateMergeRequest } = useValidateMergeRequest(
    selectedContext,
    routes
  );

  const contextActions = useCallback(
    (plan?: SummaryContext) => {
      if (plan?.type === "shipment") {
        const route = filteredRoutes.find((route) => route.id === plan.id);
        if (route) {
          return planningContextMenuActions({
            section: "shipments",
            shipmentContext: selectionToContext(plan),
            routeOrTrap: route,
            clearSelection,
            isCNCLTrapFeatureEnabled,
          });
        }
      }
      return planningContextMenuActions({
        section: "planning",
        shipmentContext: selectionToContext(plan),
        clearSelection,
        plannedRoutesOrTraps: filteredRoutes,
        validateMergeRequest,
        isCNCLTrapFeatureEnabled,
        isClosedForSequencingFlag: true,
      });
    },
    [
      clearSelection,
      filteredRoutes,
      selectionToContext,
      validateMergeRequest,
      isCNCLTrapFeatureEnabled,
    ]
  );

  return (
    <Box>
      <CollapsibleItem
        title="Routes"
        displayCount={routes.length}
        icon={tableSectionToIconMap["route"]}
        isExpanded={isExpanded}
        onExpand={(value: boolean) => setIsExpanded(value)}
      >
        <Filters
          filters={plannedSidebarRoutesAndTrapsFilters(
            routes,
            searchParams,
            serviceCenter.timeZone,
            !isFiltersLinked ? "routes" : "linked"
          )}
          direction="column"
          filterSectionId="Routes"
          searchBoxComponent={
            <SearchBox
              searchTerm={searchTerm}
              onValueChange={setSearchTerm}
              placeholder="Search for route"
            />
          }
        />
        {filteredRoutes.map((route) => (
          <Fragment key={route.id}>
            <PlanningDroppableSectionItem
              canReorder
              data={route}
              isEditingAllowed={isEditingAllowed}
              isSelected={isAllSelected(route.shipmentIds, "route")}
              selectedDate={selectedDate}
              inboundPage="planning"
              tab="routes"
              planType="delivery_route"
              icon="route"
              contextRef={contextMenuRef}
            />
          </Fragment>
        ))}
        {filteredRoutes.length === 0 && (
          <NoResultFound>
            <ContentPaste sx={{ fontSize: "2rem" }} />
            <Typography>No routes found.</Typography>
          </NoResultFound>
        )}
      </CollapsibleItem>
      <ContextMenuDropdown actions={contextActions} ref={contextMenuRef} />
    </Box>
  );
};
